/**
 * AppointmentHoverCard — Feb 2026 (v2)
 *
 * Hover popover for calendar appointment cells.  v1 used Radix HoverCard
 * but the cursor traversal from cell → popover crossed an outer
 * `pointerEvents: none` wrapper, which made Radix's auto close-delay
 * fire too early and dismiss the popover before the user could reach
 * the status dropdown.
 *
 * v2 reimplements with:
 *  - Manual ``mouseenter`` / ``mouseleave`` on BOTH the trigger wrapper
 *    AND the popover content, so cursor focus is shared.
 *  - A 350ms grace timeout: on mouseleave anywhere, we wait before
 *    closing.  Re-entering either element cancels the close.
 *  - Floating popover positioned with ``getBoundingClientRect`` so we
 *    don't depend on Radix's collision system (which was flipping side
 *    on edge slots and putting the popover behind other appointments).
 *  - z-index 1000 + portal-style fixed positioning so the popover wins
 *    over any calendar cell z-index without arguing about stacking.
 */
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { formatDateStringForDisplay } from '../../utils/dateHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled', color: 'bg-slate-100 text-slate-700' },
  { value: 'confirmed', label: 'Confirmed', color: 'bg-blue-100 text-blue-700' },
  { value: 'checked_in', label: 'Checked In', color: 'bg-teal-100 text-teal-800' },
  { value: 'completed', label: 'Completed', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'no_show', label: 'No Show', color: 'bg-amber-100 text-amber-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-rose-100 text-rose-800' },
  { value: 'client_cancelled', label: 'Client Cancelled', color: 'bg-rose-50 text-rose-700' },
  { value: 'rescheduled', label: 'Rescheduled', color: 'bg-indigo-100 text-indigo-800' },
];

const formatTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
};

const formatRange = (start, end) =>
  `${formatTime(start)} – ${formatTime(end)}`;

// Tunable timing constants.  ``CLOSE_DELAY_MS`` is the grace window the
// cursor has to travel from the appointment cell into the popover (or
// back) before we collapse the popover.  Pick a value that's long enough
// to be ergonomic but short enough that the popover doesn't feel sticky
// when the user has clearly moved on.
const OPEN_DELAY_MS = 180;
const CLOSE_DELAY_MS = 350;

export const AppointmentHoverCard = ({
  appointment,
  clientDisplayName,
  providerName,
  onStatusChange,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [status, setStatus] = useState(appointment?.status || 'scheduled');
  const [saving, setSaving] = useState(false);
  // Feb 2026 — Linked tasks fetched lazily when the popover actually
  // opens (vs eager on every calendar render — that would be N+1 per
  // appointment).  Cached on the component until the popover closes;
  // we re-fetch on next open in case anything changed.
  const [linkedTasks, setLinkedTasks] = useState([]);
  const [tasksLoaded, setTasksLoaded] = useState(false);

  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);

  // Sync local status if the parent reloads with a different value.
  useEffect(() => {
    setStatus(appointment?.status || 'scheduled');
  }, [appointment?.status, appointment?.id]);

  // Lazy-fetch linked tasks the first time the popover opens for an
  // appointment.  Subsequent opens reuse the cached list until the
  // appointment id changes.
  useEffect(() => {
    if (!open || tasksLoaded || !appointment?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/appointments/${appointment.id}/tasks`);
        if (!cancelled) setLinkedTasks(r?.data?.tasks || []);
      } catch {
        if (!cancelled) setLinkedTasks([]);
      } finally {
        if (!cancelled) setTasksLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [open, tasksLoaded, appointment?.id]);

  // Reset cache when the appointment under the hover changes.
  useEffect(() => {
    setLinkedTasks([]);
    setTasksLoaded(false);
  }, [appointment?.id]);

  const clearTimers = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  // Position the popover next to the trigger element.  Anchors to the
  // right side of the trigger; if that would overflow the viewport we
  // flip to the left.  Computed from getBoundingClientRect at the
  // moment of open so it adapts to scroll position.
  //
  // Feb 2026 — we previously read this off ``triggerRef.current`` but
  // that wrapper uses ``display: contents`` (so it doesn't break the
  // calendar's absolute-positioned grid).  display:contents elements
  // have no box, so getBoundingClientRect returned (0, 0, 0, 0) and
  // the popover landed in the top-left.  The fix is to compute from
  // the actual hovered DOM node — captured via the mouseenter event
  // target — which IS a real positioned element.
  const recomputePosition = useCallback((rect) => {
    if (!rect) return;
    const popoverWidth = 320;  // matches w-80 below
    let left = rect.right + 6;
    if (left + popoverWidth > window.innerWidth - 8) {
      left = Math.max(8, rect.left - popoverWidth - 6);
    }
    let top = rect.top;
    if (top + 280 > window.innerHeight - 8) {
      top = Math.max(8, window.innerHeight - 290);
    }
    setCoords({ top, left });
  }, []);

  const scheduleOpen = useCallback((e) => {
    clearTimers();
    // Capture the trigger rect NOW (before the open delay), because by
    // the time the timeout fires the cursor may have moved off and the
    // event target reference is gone.
    //
    // The wrapper element uses ``display: contents`` (no box), so
    // calling getBoundingClientRect on it returns (0, 0, 0, 0) — that
    // was placing the popover in the top-left corner.  Walk up from
    // ``event.target`` until we find a real positioned ancestor with
    // a non-zero rect; that's the appointment cell.
    let rect = null;
    let node = e?.target;
    while (node && node !== document.body) {
      if (typeof node.getBoundingClientRect === 'function') {
        const r = node.getBoundingClientRect();
        if (r && r.width > 0 && r.height > 0) {
          rect = r;
          break;
        }
      }
      node = node.parentElement;
    }
    openTimerRef.current = setTimeout(() => {
      recomputePosition(rect);
      setOpen(true);
    }, OPEN_DELAY_MS);
  }, [clearTimers, recomputePosition]);

  const scheduleClose = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
    }, CLOSE_DELAY_MS);
  }, []);

  // Cancel close if cursor re-enters either trigger or popover.
  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const currentStatusMeta = useMemo(
    () => STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0],
    [status],
  );

  const handleStatusChange = async (newStatus) => {
    if (newStatus === status) return;
    const prev = status;
    setStatus(newStatus);
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/appointments/${appointment.id}`, {
        status: newStatus,
      });
      const label = STATUS_OPTIONS.find(s => s.value === newStatus)?.label || newStatus;
      toast.success(`Status set to ${label}`);
      if (onStatusChange) onStatusChange(r?.data || { ...appointment, status: newStatus });
    } catch (err) {
      setStatus(prev);
      toast.error(err?.response?.data?.detail || 'Could not update status.');
    } finally {
      setSaving(false);
    }
  };

  // Children get cloned with the same ref + hover handlers, so we don't
  // need to change the caller's JSX — just wrap with this component.
  const trigger = (
    <div
      ref={triggerRef}
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
      style={{ display: 'contents' }}
    >
      {children}
    </div>
  );

  return (
    <>
      {trigger}
      {open && createPortal(
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            width: 320,
            zIndex: 1000,
            // Feb 2026 — outer popover is click-through so it can't
            // intercept a drag-start on an appointment that happens
            // to sit behind it.  The interactive controls inside
            // (status select, linked task buttons) opt back in via
            // `pointerEvents: 'auto'`.  Result: hover info is fully
            // visible, but dragging adjacent appointments is no
            // longer blocked.
            pointerEvents: 'none',
          }}
          className="rounded-md border bg-white text-slate-900 shadow-xl p-3"
          data-testid="appointment-hover-card"
        >
          <div className="space-y-2.5">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">
                  {clientDisplayName || 'Appointment'}
                </div>
                <div className="text-xs text-slate-500">
                  {formatRange(appointment?.start_time, appointment?.end_time)}
                </div>
              </div>
              <Badge className={currentStatusMeta.color}>{currentStatusMeta.label}</Badge>
            </div>

            {providerName && (
              <div className="text-xs text-slate-600">
                <span className="font-medium">Provider:</span> {providerName}
              </div>
            )}
            {appointment?.service_type && (
              <div className="text-xs text-slate-600">
                <span className="font-medium">Service:</span> {appointment.service_type}
              </div>
            )}
            {(appointment?.location || appointment?.telehealth_modality) && (
              <div className="text-xs text-slate-600">
                <span className="font-medium">Location:</span>{' '}
                {appointment.telehealth_modality || appointment.location}
              </div>
            )}
            {appointment?.notes && (
              <div className="text-xs text-slate-600 line-clamp-2">
                <span className="font-medium">Notes:</span> {appointment.notes}
              </div>
            )}

            {/* Linked tasks (Feb 2026) — surfaces tasks spawned from
                create_task fields on this appointment's clinical
                note(s), or any task explicitly tied to this
                appointment_id.  Lets the provider see "Reschedule"
                / follow-up items at a glance without leaving the
                calendar. */}
            {linkedTasks.length > 0 && (
              <div
                className="pt-2 border-t border-slate-200"
                style={{ pointerEvents: 'auto' }}
                onMouseEnter={cancelClose}
                onMouseLeave={scheduleClose}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1">
                  Linked tasks ({linkedTasks.length})
                </div>
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                  {linkedTasks.slice(0, 4).map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className="w-full text-left text-xs rounded border border-slate-200 hover:border-blue-400 hover:bg-blue-50 px-2 py-1 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.dispatchEvent(new CustomEvent('openTaskDrawer', {
                          detail: { task_id: t.id },
                        }));
                      }}
                      data-testid={`hover-linked-task-${t.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium truncate">{t.title || 'Task'}</div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          t.status === 'completed' ? 'bg-emerald-100 text-emerald-800'
                          : t.status === 'in_progress' ? 'bg-blue-100 text-blue-800'
                          : t.status === 'cancelled' || t.status === 'dismissed' ? 'bg-slate-100 text-slate-600'
                          : 'bg-amber-100 text-amber-800'
                        }`}>
                          {t.status || 'pending'}
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-500 flex flex-wrap gap-x-2">
                        {t.assigned_to_name && <span>→ {t.assigned_to_name}</span>}
                        {t.due_date && <span>Due {formatDateStringForDisplay(t.due_date)}</span>}
                      </div>
                    </button>
                  ))}
                  {linkedTasks.length > 4 && (
                    <div className="text-[10px] text-slate-400 px-2">
                      +{linkedTasks.length - 4} more — open appointment to view all
                    </div>
                  )}
                </div>
              </div>
            )}
            {open && tasksLoaded && linkedTasks.length === 0 && (
              <div className="pt-2 border-t border-slate-200 text-[11px] text-slate-400 italic">
                No linked tasks
              </div>
            )}

            <div
              className="pt-2 border-t border-slate-200"
              style={{ pointerEvents: 'auto' }}
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1">
                Change status
              </label>
              <select
                value={status}
                onChange={(e) => handleStatusChange(e.target.value)}
                disabled={saving}
                className="w-full text-xs border border-slate-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                data-testid={`appointment-hover-status-${appointment?.id}`}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              {saving && (
                <div className="text-[10px] text-slate-400 mt-1">Saving…</div>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default AppointmentHoverCard;
